import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import useSWR from 'swr';
import { CalendarBlankIcon, CaretLeftIcon, CaretRightIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';

import { PageHeader } from '../../../../components/PageHeader';
import { PageHeading } from '../../../../components/PageHeading';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { SpinnerBlock } from '../../../../components/Spinner';
import { Input } from '../../../../components/input/Input';
import { Button } from '../../../../components/button/Button';
import { Dialog } from '../../../../components/dialog/Dialog';
import { EmptyState } from '../../../../components/empty-state/EmptyState';
import { ErrorState } from '../../../../components/error-state/ErrorState';
import classNames from '../../../../utils/classnames';
import { getDisplayError } from '../../../../utils/get-display-error';
import { formatDayMonth, formatInputDate } from '../../../../utils/date';
import { useSearchParam } from '../../../../hooks/useSearchParam';
import {
  getPlanningBoardWeekly,
  setPlanningBoardWeeklyTarget,
  type PlanningBoardWeeklyResponse,
  type PlanningBoardWeeklySaveDayResponse,
  type WeeklyTrailer,
  type WeeklyTrip,
  type WeeklyTruck,
} from '../../models';
import { addDays, getIsoWeek, getWeekDays, getWeekStart, isIsoDate } from '../../weekly';
import { PlannerSelect } from '../components/PlannerSelect';
import { DayPanel } from './DayPanel';
import { hasUnsavedChanges, isSameTrailer, toTripDrafts, type TripDraft } from './tripDrafts';
import { StatusLegend } from './TripStatus';
import { WeeklyBoardTable, type WeeklyCell } from './WeeklyBoardTable';
import { BAR_FILL_CLASS } from './weekTarget';

function truckMatchesWeeklySearch(truck: WeeklyTruck, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    truck.supplierName,
    truck.licensePlate,
    ...truck.trips.flatMap((t) => [t.loading, t.unloading]),
    ...truck.trailers.flatMap((d) => [d.trailer.name, d.trailer.licensePlate]),
  ].some((value) => value.toLowerCase().includes(q));
}

function tripsOn(truck: WeeklyTruck, date: string): WeeklyTrip[] {
  return truck.trips.filter((t) => t.date === date);
}

function trailerOn(truck: WeeklyTruck, date: string): WeeklyTrailer | null {
  return truck.trailers.find((t) => t.date === date)?.trailer ?? null;
}

/** The trailer the truck last pulled before `date` this week; `trailers` is sorted by date. */
function lastTrailerBefore(truck: WeeklyTruck, date: string): WeeklyTrailer | null {
  return truck.trailers.findLast((t) => t.date < date)?.trailer ?? null;
}

function openPanel(truck: WeeklyTruck, cell: WeeklyCell): OpenPanel {
  const trailer = trailerOn(truck, cell.date) ?? lastTrailerBefore(truck, cell.date);
  return { cell, drafts: toTripDrafts(tripsOn(truck, cell.date)), trailer, initialTrailer: trailer };
}

interface OpenPanel {
  cell: WeeklyCell;
  drafts: TripDraft[];
  trailer: WeeklyTrailer | null;
  /** The trailer the panel opened with: the day's own, or else the one carried over from an earlier day. */
  initialTrailer: WeeklyTrailer | null;
}

interface WeeklyPlanningBoardProps {
  viewToggle: React.ReactNode;
}

export const WeeklyPlanningBoard: React.FC<WeeklyPlanningBoardProps> = ({ viewToggle }) => {
  const [dailyDate] = useSearchParam('date', formatInputDate(new Date()));
  const [weekParam, setWeekParam] = useSearchParam('week', dailyDate);
  // A hand-edited URL can hold anything; fall back to the current week instead of crashing on it.
  const weekStart = getWeekStart(isIsoDate(weekParam) ? weekParam : formatInputDate(new Date()));
  const days = getWeekDays(weekStart);
  const { week, year } = getIsoWeek(weekStart);

  const [plannerIds, setPlannerIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [panel, setPanel] = useState<OpenPanel | null>(null);
  /** Navigation held back until the planner confirms discarding unsaved panel changes. */
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  const swrKey = ['planning-board-weekly', weekStart];
  const { data, error, isLoading, mutate } = useSWR(swrKey, () => getPlanningBoardWeekly(weekStart), {
    revalidateOnFocus: false,
  });

  const trucks = useMemo(
    () =>
      (data?.trucks ?? []).filter(
        (truck) =>
          (plannerIds.length === 0 || (truck.planner != null && plannerIds.includes(truck.planner.id))) &&
          truckMatchesWeeklySearch(truck, search),
      ),
    [data?.trucks, plannerIds, search],
  );

  const findTruck = (truckId: string) => data?.trucks.find((t) => t.id === truckId);
  const selected = panel?.cell ?? null;
  const selectedTruck = selected ? findTruck(selected.truckId) : undefined;
  const tripsChanged =
    panel != null && selectedTruck != null && hasUnsavedChanges(panel.drafts, tripsOn(selectedTruck, panel.cell.date));
  // A carried-over trailer can be saved as is, but leaving it unsaved does not need a confirmation.
  const panelDiffersFromSaved =
    tripsChanged ||
    (panel != null &&
      selectedTruck != null &&
      !isSameTrailer(panel.trailer, trailerOn(selectedTruck, panel.cell.date)));
  const isPanelDirty = tripsChanged || (panel != null && !isSameTrailer(panel.trailer, panel.initialTrailer));

  const leavePanel = (navigation: () => void) => {
    if (isPanelDirty) setPendingNavigation(() => navigation);
    else navigation();
  };

  const selectCell = (cell: WeeklyCell | null) => {
    if (cell && selected && cell.truckId === selected.truckId && cell.date === selected.date) return;
    const truck = cell ? findTruck(cell.truckId) : undefined;
    leavePanel(() => setPanel(cell && truck ? openPanel(truck, cell) : null));
  };

  const goToWeek = (nextWeekStart: string) =>
    leavePanel(() => {
      setPanel(null);
      setWeekParam(nextWeekStart);
    });

  const updateTruck = (truckId: string, update: (truck: WeeklyTruck) => WeeklyTruck) =>
    mutate(
      (current: PlanningBoardWeeklyResponse | undefined) =>
        current && { ...current, trucks: current.trucks.map((t) => (t.id === truckId ? update(t) : t)) },
      { revalidate: false },
    );

  const handleDaySaved = (cell: WeeklyCell, saved: PlanningBoardWeeklySaveDayResponse) => {
    setPanel(null);
    return updateTruck(cell.truckId, (truck) => ({
      ...truck,
      trips: [...truck.trips.filter((t) => t.date !== cell.date), ...saved.trips].sort(
        (a, b) => a.date.localeCompare(b.date) || a.sequence - b.sequence,
      ),
      trailers: [
        ...truck.trailers.filter((t) => t.date !== cell.date),
        ...(saved.trailer ? [{ date: cell.date, trailer: saved.trailer }] : []),
      ].sort((a, b) => a.date.localeCompare(b.date)),
    }));
  };

  const handleTargetChange = async (truckId: string, targetKm: number) => {
    try {
      await setPlanningBoardWeeklyTarget({ supplierTruckId: truckId, weekStart, km: targetKm });
      await updateTruck(truckId, (truck) => ({ ...truck, targetKm }));
    } catch (err) {
      toast.error(`Kon target niet opslaan: ${getDisplayError(err)}`);
    }
  };

  const dayIdx = selected ? days.indexOf(selected.date) : -1;
  const moveDay = (offset: number) => {
    if (!selected) return undefined;
    const date = days[dayIdx + offset];
    return date ? () => selectCell({ truckId: selected.truckId, date }) : undefined;
  };

  return (
    <div className="-mb-32 flex h-screen flex-col">
      <PageHeader title="Planning Board" />

      <PageHeading
        noMarginBottom
        leftSide={
          <Breadcrumb
            currentItem="Planning board"
            meta={`Week ${week} · ${formatDayMonth(days[0])} – ${formatDayMonth(days[days.length - 1])} ${year}`}
          />
        }
        rightSide={
          <>
            {viewToggle}
            <PlannerSelect planners={data?.planners ?? []} selectedIds={plannerIds} onChange={setPlannerIds} />
            <div className="w-60">
              <Input
                type="text"
                value={search}
                onChange={setSearch}
                placeholder="Zoek vervoerder, kenteken…"
                iconLeft={<MagnifyingGlassIcon className="input-icon" />}
              />
            </div>
            <WeekNav
              label={`Week ${week} — ${year}`}
              onPrev={() => goToWeek(addDays(weekStart, -7))}
              onNext={() => goToWeek(addDays(weekStart, 7))}
              onToday={() => goToWeek(getWeekStart(formatInputDate(new Date())))}
            />
          </>
        }
      />

      <div className="relative min-h-0 flex-1">
        <div className={classNames('h-full overflow-auto p-6', selected && 'pr-[424px]')}>
          {error ? (
            <ErrorState error={error} onRetry={() => mutate()} variant="section" />
          ) : isLoading ? (
            <SpinnerBlock message="Weekplanning laden…" />
          ) : trucks.length === 0 ? (
            <EmptyState
              variant="panel"
              icon={<MagnifyingGlassIcon />}
              title={search.trim() ? `Geen vervoerders gevonden voor "${search.trim()}"` : 'Geen vervoerders gevonden'}
              description={search.trim() ? 'Pas je zoekopdracht aan of wis het zoekveld.' : undefined}
            />
          ) : (
            <div className="flex flex-col gap-4">
              <WeeklyBoardTable
                days={days}
                trucks={trucks}
                selected={selected}
                onSelectCell={selectCell}
                onTargetChange={handleTargetChange}
              />
              <div className="flex flex-wrap items-center justify-between gap-4">
                <StatusLegend />
                <TargetLegend />
              </div>
            </div>
          )}
        </div>

        {panel && selectedTruck && (
          <DayPanel
            key={`${panel.cell.truckId}-${panel.cell.date}`}
            truck={selectedTruck}
            date={panel.cell.date}
            drafts={panel.drafts}
            onDraftsChange={(drafts) => setPanel({ ...panel, drafts })}
            trailer={panel.trailer}
            onTrailerChange={(trailer) => setPanel({ ...panel, trailer })}
            canSave={panelDiffersFromSaved}
            onClose={() => selectCell(null)}
            onPrevDay={moveDay(-1)}
            onNextDay={moveDay(1)}
            onSaved={(saved) => handleDaySaved(panel.cell, saved)}
          />
        )}
      </div>

      <Dialog
        isOpen={pendingNavigation != null}
        onClose={() => setPendingNavigation(null)}
        title="Wijzigingen verwerpen?"
      >
        <p className="text-sm text-fg-secondary">De wijzigingen van deze dag zijn nog niet opgeslagen.</p>
        <div className="mt-6 flex justify-end gap-2">
          <Button color="ghost" onTrigger={() => setPendingNavigation(null)}>
            Annuleer
          </Button>
          <Button
            color="danger"
            onTrigger={() => {
              pendingNavigation?.();
              setPendingNavigation(null);
            }}
          >
            Verwerpen
          </Button>
        </div>
      </Dialog>
    </div>
  );
};

interface WeekNavProps {
  label: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
}

const WeekNav: React.FC<WeekNavProps> = ({ label, onPrev, onNext, onToday }) => (
  <div className="flex h-9 items-stretch overflow-hidden rounded-md border border-border bg-surface">
    <button
      type="button"
      title="Vorige week"
      aria-label="Vorige week"
      onClick={onPrev}
      className="flex w-8 items-center justify-center border-r border-border text-fg-secondary transition-colors hover:bg-surface-hover"
    >
      <CaretLeftIcon className="h-3.5 w-3.5" />
    </button>
    <button
      type="button"
      title="Naar deze week"
      onClick={onToday}
      className="flex items-center gap-2 px-3.5 text-sm font-medium text-fg transition-colors hover:bg-surface-hover"
    >
      <CalendarBlankIcon className="h-3.5 w-3.5 text-fg-muted" />
      {label}
    </button>
    <button
      type="button"
      title="Volgende week"
      aria-label="Volgende week"
      onClick={onNext}
      className="flex w-8 items-center justify-center border-l border-border text-fg-secondary transition-colors hover:bg-surface-hover"
    >
      <CaretRightIcon className="h-3.5 w-3.5" />
    </button>
  </div>
);

const LEGEND = [
  { label: '≥ 90%', className: BAR_FILL_CLASS.positive },
  { label: '75 – 90%', className: BAR_FILL_CLASS.warning },
  { label: '< 75%', className: BAR_FILL_CLASS.negative },
];

const TargetLegend: React.FC = () => (
  <div className="flex items-center gap-4 text-2xs text-fg-muted">
    <span>% van weektarget</span>
    {LEGEND.map((item) => (
      <span key={item.label} className="flex items-center gap-1.5">
        <span className={classNames('h-1.5 w-3 rounded-full', item.className)} />
        {item.label}
      </span>
    ))}
  </div>
);
